import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { userApi } from '@features/login';
import { hashEmail } from '@features/shared';

const MFA_SESSION_EXPIRATION_MINUTES = 60;

interface MfaStore {
  // 상태 데이터
  sessions: Record<string, number>; // 해시화된 이메일 -> 만료 시간

  // 메서드들
  setMfaSession: ({ email }: { email: string }) => void;
  hasMfaSession: ({ email }: { email: string }) => boolean;
  clearMfaSession: () => void;

  checkMfaException: ({ email }: { email: string }) => Promise<boolean>;
  checkMfaBioRequired: ({ email }: { email: string }) => Promise<boolean>;
  executeMfaLogin: ({
    email,
    uid,
    uidSignature,
    signatureTimestamp,
  }: {
    email: string;
    uid: string;
    uidSignature: string;
    signatureTimestamp: string;
  }) => Promise<string>;
  validateJwtTokenResponse: ({
    jwtTokenResponse,
    isBioOnly,
  }: {
    jwtTokenResponse: string;
    isBioOnly: boolean;
  }) => Promise<string>;
}

export const useMfaStore = create<MfaStore>()(
  persist(
    (set, get) => ({
      // 상태 데이터
      sessions: {},

      // 로그인 성공 시 MFA 세션 저장
      setMfaSession: ({ email }) => {
        const hashedKey = hashEmail(email);
        const expirationTime = Date.now() + MFA_SESSION_EXPIRATION_MINUTES * 60 * 1000;

        set((state) => ({
          sessions: {
            ...state.sessions,
            [hashedKey]: expirationTime,
          },
        }));
      },

      // MFA 세션이 존재하는지 확인
      hasMfaSession: ({ email }) => {
        const hashedKey = hashEmail(email);
        const state = get();
        const expirationTime = state.sessions[hashedKey];

        if (!expirationTime) {
          return false;
        }

        if (Date.now() > expirationTime) {
          // 만료된 세션 제거
          set((state) => {
            const newSessions = { ...state.sessions };
            delete newSessions[hashedKey];
            return { sessions: newSessions };
          });
          return false;
        }

        return true;
      },

      // 만료된 MFA 세션 삭제
      clearMfaSession: () => {
        const state = get();
        const now = Date.now();
        const validSessions: Record<string, number> = {};

        // 만료되지 않은 세션만 유지
        Object.entries(state.sessions).forEach(([key, expTime]) => {
          if (expTime > now) {
            validSessions[key] = expTime;
          }
        });

        set({ sessions: validSessions });
      },

      // MFA 예외 여부 확인
      checkMfaException: async ({ email }) => {
        try {
          const { isMfaException } = await userApi.getMfaException({ email });
          return isMfaException;
        } catch (error) {
          console.error('[Error] check MFA exception:', error);
          return false;
        }
      },

      // MFA BioRequired 여부 확인
      checkMfaBioRequired: async ({ email }) => {
        try {
          const bioRequiredChannelList = await userApi.getBioRequiredChannelList();
          const { channelList } = await userApi.getUserChannelList({ email });
          const isMfaException = await get().checkMfaException({ email });

          const hasBioRequiredChannel = channelList.some((channel: string) => bioRequiredChannelList.includes(channel));
          const isMfaRequired = hasBioRequiredChannel && !isMfaException;

          return isMfaRequired;
        } catch (error) {
          console.error('[Error] check MFA bio required:', error);
          return false;
        }
      },

      // MFA 로그인 실행
      executeMfaLogin: async ({ email, uid }) => {
        const isBioOnly = await get().checkMfaBioRequired({ email });

        const url = new URL(window.location.href);
        url.searchParams.delete('gig_events');
        const cleanUrl = url.toString();

        try {
          const jwtTokenResponse = await userApi.requestMfaLogin({
            email,
            uid,
            isBioOnly,
            returnUrl: `${cleanUrl}&isBioOnly=${isBioOnly}`,
          });

          return jwtTokenResponse;
        } catch (error) {
          console.error('[Error] execute MFA login:', error);
        }
      },

      // JWT Response 검증
      validateJwtTokenResponse: async ({ jwtTokenResponse, isBioOnly }) => {
        try {
          const isValidate = await userApi.validateJwtToken({
            jwtTokenResponse,
            isBioOnly,
          });

          return isValidate;
        } catch (error) {
          console.error('[Error] validate JWT token:', error);
          return false;
        }
      },
    }),
    {
      name: 'mfa-store', // localStorage에 저장될 키 이름
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
